import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  CheckSquare,
  Square,
  Loader2,
  CheckCircle2,
  XCircle,
  Wallet,
  RefreshCw,
  Landmark,
  ShieldCheck,
  Eye,
  X,
  Phone,
  MapPin,
  Users,
  Search,
} from "lucide-react";
import { BASE_URL } from "../api";
import { PaidFamilyTransactionsContent } from "./PaidFamilyTransactions.jsx";

const API_BASE = `${BASE_URL}/api`;
const MAX_SELECT = 200;

/**
 * MFI Finance Activation — /billing/mfi-finance
 *
 * Flow: client dropdown -> package dropdown -> pending families -> Activate
 * Tabs: Pending Activation | Activated | Transactions (finance team) | Paid Family Transactions
 *
 * Endpoints:
 *   GET  /mfi-clients/
 *   GET  /mfi-clients/{client_id}/packages/          -> { client: { packages: [...] } }
 *   GET  /finance/families/pending/?client_id=&package=   -> { families: [...] }
 *   GET  /finance/families/activated/?client_id=&package= -> { families: [...] }
 *   POST /finance/families/bulk-activate/
 *        body: { family_ids: [db ids], received_transaction_id, received_payment_method, received_bank_name }
 *        (used by this page for ALL activations — one request for the whole selection)
 *   POST /finance/families/{id}/activate/  (single-family endpoint — unchanged, kept for other parts of the app)
 *   POST /finance/families/{id}/paid/  body: { payment_transaction_id }
 *   GET  /finance/transactions/?client_id=&package=&status=&transaction_type=
 *   Paid Family Transactions tab reuses PaidFamilyTransactionsContent
 *   (GET /paid-family-transactions/ + filters + detail modal).
 */

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function pick(obj, keys, fallback = "") {
  if (!obj || typeof obj !== "object") return fallback;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null && obj[key] !== "") return obj[key];
  }
  return fallback;
}

function toArrayPayload(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.clients)) return data.clients;
  if (Array.isArray(data.packages)) return data.packages;
  if (Array.isArray(data.families)) return data.families;
  if (Array.isArray(data.transactions)) return data.transactions;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.transaction)) return data.transaction;
  if (data.transaction && typeof data.transaction === "object") return [data.transaction];
  if (data.client && Array.isArray(data.client.packages)) return data.client.packages;
  if (data.data && Array.isArray(data.data.packages)) return data.data.packages;
  if (data.data && Array.isArray(data.data.families)) return data.data.families;
  return [];
}

function normalizeClient(raw) {
  return {
    id: pick(raw, ["client_id", "id", "pk", "uuid"]),
    name: pick(raw, ["name", "client_name", "title", "label"], "Unnamed Client"),
  };
}

// Many packages share the name "Package 1", so the label adds price + dates to tell them apart.
function normalizePackage(raw) {
  const name = pick(raw, ["name", "package_name", "title", "label"], "Unnamed Package");
  const price = pick(raw, ["price"], "");
  const start = pick(raw, ["start_date"], "");
  const end = pick(raw, ["end_date"], "");
  const dates = start ? (end && end !== start ? `${start} → ${end}` : start) : "";
  const label = [name, price !== "" ? `₹${price}` : "", dates, raw?.is_completed ? "Completed" : ""]
    .filter(Boolean)
    .join(" · ");
  return { id: pick(raw, ["id", "package_id", "pkg_id", "pk", "uuid"]), name, price, label };
}

function normalizeMember(m) {
  const name =
    pick(m, ["full_name"], "") ||
    [m?.first_name, m?.middle_name, m?.last_name].filter(Boolean).join(" ") ||
    "—";
  return {
    id: pick(m, ["id", "member_id"], name),
    memberId: pick(m, ["member_id"], ""),
    name,
    dob: pick(m, ["date_of_birth"], ""),
    age: pick(m, ["age"], ""),
    gender: pick(m, ["gender"], ""),
    relationship: pick(m, ["relationship"], ""),
    loanNumber: pick(m, ["loan_number"], ""),
    mobile: pick(m, ["mobile"], ""),
    altMobile: pick(m, ["alternate_mobile"], ""),
    email: pick(m, ["email"], ""),
    photo: pick(m, ["photo"], ""),
    isPrimary: !!m?.is_primary_contact,
  };
}

function normalizeFamily(raw, pkg) {
  const familyIdStr = pick(raw, ["family_id", "familyId"], "");
  const head = pick(raw, ["family_head", "head_name", "familyHead", "family_name", "head", "name"], "");
  const address = raw?.address || null;
  const members = Array.isArray(raw?.members) ? raw.members.map(normalizeMember) : [];
  const mc = Array.isArray(raw?.members)
    ? raw.members.length
    : pick(raw, ["member_count", "memberCount", "total_members"], null);
  // Patient name from response members (first_name [+ middle/last_name]); primary first.
  const primaryMember = members.find((m) => m.isPrimary) || members[0] || null;
  const patientName = (primaryMember?.name && primaryMember.name !== "—" ? primaryMember.name : "") || head || familyIdStr || "—";
  return {
    members,
    patientName,
    packagePrice: pick(raw, ["package_price"], pkg?.price ?? ""),
    createdAt: pick(raw, ["created_at", "createdAt"], null),
    id: pick(raw, ["id", "pk", "uuid"], familyIdStr),
    // NEW: the real database ID (never the display family_id). Used for bulk activation.
    dbId: pick(raw, ["id", "pk"], null),
    familyIdStr,
    familyHead: head || familyIdStr || "—",
    memberCount: mc === null ? "—" : mc,
    // Pending/activated APIs return the package once at top level, so fall back to it
    packageName: pick(raw, ["package_name", "packageName"], pkg?.name || "—"),
    primaryMobile: pick(raw, ["primary_mobile", "mobile", "phone"], ""),
    address,
    activatedAt: pick(raw, ["finance_activated_at", "activated_at", "activatedAt"], null),
  };
}

function extractFamilies(data) {
  const list = toArrayPayload(data);
  const pkg = data?.package || null;
  return list.map((f) => normalizeFamily(f, pkg));
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatAmount(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return v ?? "—";
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function addressLine(a) {
  if (!a) return "";
  return [a.village, a.block, a.district, a.state].filter(Boolean).join(", ");
}

// ---------------------------------------------------------------------------
// SearchableSelect — reusable searchable dropdown matching existing styling
// ---------------------------------------------------------------------------
function SearchableSelect({
  label,
  placeholder,
  searchPlaceholder,
  options,
  value,
  onChange,
  getOptionLabel,
  getOptionValue,
  getOptionSearchText,
  disabled = false,
  disabledPlaceholder = "Select a client first",
  loading = false,
  error = null,
  onRetry = null,
  emptyText = "No options found.",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selectedOption = useMemo(() => options.find((o) => String(getOptionValue(o)) === String(value)) || null, [options, value, getOptionValue]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const text = (getOptionSearchText ? getOptionSearchText(o) : getOptionLabel(o)).toLowerCase();
      return text.includes(q);
    });
  }, [options, query, getOptionLabel, getOptionSearchText]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setQuery("");
    }
  }, [open]);

  // Keep selected visible when reopening - reset query so user sees full list initially
  const handleToggle = () => {
    if (disabled || loading || error) return;
    setOpen((v) => !v);
  };

  const handleSelect = (opt) => {
    const v = String(getOptionValue(opt));
    // mimic native select event shape
    onChange({ target: { value: v } });
    setOpen(false);
    setQuery("");
  };

  const handleClearSelection = (e) => {
    e.stopPropagation();
    onChange({ target: { value: "" } });
    setOpen(false);
    setQuery("");
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-400 py-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading...
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center gap-2">
        <div className="text-sm text-red-600 flex-1">{error}</div>
        {onRetry && (
          <button onClick={onRetry} className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm hover:bg-slate-50">
            Retry
          </button>
        )}
      </div>
    );
  }

  if (disabled) {
    return (
      <div className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-slate-50 text-slate-400 cursor-not-allowed">
        {disabledPlaceholder}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={handleToggle}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-white text-left focus:outline-none focus:ring-2 focus:ring-indigo-500 hover:border-slate-300 transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selectedOption ? "text-slate-800 truncate" : "text-slate-400 truncate"}>
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {selectedOption && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClearSelection}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleClearSelection(e)}
              className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
              title="Clear selection"
              aria-label="Clear selection"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <svg className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>

      {open && (
        <div className="absolute z-20 mt-1.5 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-9 pr-8 py-2 rounded-lg border border-slate-200 text-sm bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder:text-slate-400"
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-slate-400"
                  aria-label="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <div className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-slate-400">
                {query ? `No results for "${query}"` : emptyText}
              </div>
            ) : (
              filtered.map((opt) => {
                const isSelected = String(getOptionValue(opt)) === String(value);
                return (
                  <button
                    key={String(getOptionValue(opt))}
                    onClick={() => handleSelect(opt)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between gap-2 ${isSelected ? "bg-indigo-50 text-indigo-700 font-medium" : "text-slate-700"}`}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <span className="truncate">{getOptionLabel(opt)}</span>
                    {isSelected && <CheckCircle2 className="w-4 h-4 text-indigo-600 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
          {filtered.length > 0 && (
            <div className="px-3 py-1.5 border-t border-slate-100 text-[11px] text-slate-400 bg-slate-50">
              {filtered.length} of {options.length} {options.length === 1 ? "option" : "options"}
              {query ? ` matching "${query}"` : ""}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------
function Toast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [toast, onClose]);

  if (!toast) return null;
  const isError = toast.type === "error";
  return (
    <div
      className={`fixed top-5 right-5 z-[60] flex items-start gap-3 rounded-xl border px-4 py-3 shadow-lg max-w-sm ${
        isError ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"
      }`}
      role="status"
    >
      {isError ? (
        <XCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
      ) : (
        <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
      )}
      <div className={`text-sm font-medium ${isError ? "text-red-800" : "text-emerald-800"}`}>{toast.message}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Auth + request helper (token from localStorage)
// ---------------------------------------------------------------------------
const getAuthHeader = (token) => {
  if (!token) return {};
  if (token.split(".").length === 3) return { Authorization: `Bearer ${token}` };
  return { Authorization: `Token ${token}` };
};

// CHANGED (backward compatible): thrown errors now also carry `status` and `data`
// (the parsed error body) so the bulk-activate handler can read any
// activated_family_ids the backend returned alongside an error status.
const apiRequest = async (url, options = {}) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Authentication required. Please login again.");

  const headers = {
    ...getAuthHeader(token),
    ...(options.body && !(options.body instanceof FormData) ? { "Content-Type": "application/json" } : {}),
    ...(options.headers || {}),
  };

  const res = await fetch(url, { ...options, headers });

  if (res.status === 401) {
    localStorage.removeItem("token");
    const authErr = new Error("Session expired. Please login again.");
    authErr.status = 401;
    throw authErr;
  }
  if (!res.ok) {
    let msg = "Request failed";
    let errData = null;
    try {
      errData = await res.json();
      msg = errData.detail || errData.message || errData.error || JSON.stringify(errData);
    } catch {
      msg = `Request failed (${res.status})`;
    }
    const httpErr = new Error(msg);
    httpErr.status = res.status;
    httpErr.data = errData;
    throw httpErr;
  }
  if (res.status === 204) return null;
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

// ---------------------------------------------------------------------------
// Shared table pieces
// ---------------------------------------------------------------------------
function FamilyCell({ fam }) {
  const sub = [fam.primaryMobile, addressLine(fam.address)].filter(Boolean).join(" · ");
  return (
    <>
      <div className="font-medium text-slate-800">{fam.familyHead}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
      {fam.familyIdStr && fam.familyHead !== fam.familyIdStr && (
        <div className="text-[11px] text-slate-400">{fam.familyIdStr}</div>
      )}
    </>
  );
}

// Pending Activation shows patient name (members[].first_name) instead of family head.
function PatientCell({ fam }) {
  const sub = [fam.primaryMobile, addressLine(fam.address)].filter(Boolean).join(" · ");
  return (
    <>
      <div className="font-medium text-slate-800">{fam.patientName || fam.familyHead}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
      {fam.familyIdStr && (
        <div className="text-[11px] text-slate-400">{fam.familyIdStr}</div>
      )}
    </>
  );
}

function StateRow({ colSpan, loading, error, empty, loadingText, emptyText, onRetry }) {
  if (loading)
    return (
      <tr>
        <td colSpan={colSpan} className="px-6 py-10 text-center text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
          {loadingText}
        </td>
      </tr>
    );
  if (error)
    return (
      <tr>
        <td colSpan={colSpan} className="px-6 py-10 text-center">
          <div className="text-sm text-red-600 mb-2">{error}</div>
          <button onClick={onRetry} className="px-4 py-1.5 rounded-lg bg-white border border-slate-200 text-sm hover:bg-slate-50">
            Retry
          </button>
        </td>
      </tr>
    );
  if (empty)
    return (
      <tr>
        <td colSpan={colSpan} className="px-6 py-10 text-center text-slate-400">
          {emptyText}
        </td>
      </tr>
    );
  return null;
}

// ---------------------------------------------------------------------------
// Family details modal (opened from the View button)
// ---------------------------------------------------------------------------
function FamilyModal({ fam, onClose }) {
  useEffect(() => {
    if (!fam) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fam, onClose]);

  if (!fam) return null;
  const a = fam.address || {};
  const addressRows = [
    ["Address line 1", a.address_line_1],
    ["Address line 2", a.address_line_2],
    ["Village", a.village],
    ["Gram panchayat", a.gram_panchayat],
    ["Block", a.block],
    ["District", a.district],
    ["State", a.state],
    ["Pincode", a.pincode],
  ].filter(([, v]) => v);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-slate-100">
          <div>
            <h3 className="text-base font-semibold text-slate-800">{fam.familyIdStr || `Family #${fam.id}`}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {fam.packageName}
              {fam.packagePrice !== "" && fam.packagePrice !== null ? ` · ${formatAmount(fam.packagePrice)}` : ""}
              {fam.createdAt ? ` · Registered ${formatDate(fam.createdAt)}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Pending</span>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto px-6 py-5 space-y-6">
          {/* Contact + address */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-600 mb-2">
                <Phone className="w-3.5 h-3.5" /> Primary mobile
              </div>
              <div className="text-sm text-slate-800">{fam.primaryMobile || "—"}</div>
            </div>
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-600 mb-2">
                <MapPin className="w-3.5 h-3.5" /> Address
              </div>
              {addressRows.length === 0 ? (
                <div className="text-sm text-slate-400">—</div>
              ) : (
                <dl className="text-sm space-y-1">
                  {addressRows.map(([k, v]) => (
                    <div key={k} className="flex gap-2">
                      <dt className="text-slate-400 w-32 shrink-0">{k}</dt>
                      <dd className="text-slate-800">{v}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          </div>

          {/* Members */}
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-2">
              <Users className="w-4 h-4 text-indigo-600" /> Family members ({fam.members.length})
            </div>
            {fam.members.length === 0 ? (
              <div className="text-sm text-slate-400 rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center">
                No active members in this family.
              </div>
            ) : (
              <div className="rounded-xl border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 text-left text-xs">
                      <th className="px-4 py-2.5 font-medium">Member</th>
                      <th className="px-3 py-2.5 font-medium">Relation</th>
                      <th className="px-3 py-2.5 font-medium">Age / Gender</th>
                      <th className="px-3 py-2.5 font-medium">Loan no.</th>
                      <th className="px-3 py-2.5 font-medium">Contact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fam.members.map((m) => (
                      <tr key={String(m.id)} className="border-t border-slate-100 align-top">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {m.photo ? (
                              <img src={m.photo} alt={m.name} className="w-9 h-9 rounded-full object-cover border border-slate-200" />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-semibold">
                                {(m.name || "?").charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div>
                              <div className="font-medium text-slate-800">
                                {m.name}
                                {m.isPrimary && (
                                  <span className="ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 text-indigo-700">
                                    Primary
                                  </span>
                                )}
                              </div>
                              {m.memberId && <div className="text-[11px] text-slate-400">{m.memberId}</div>}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-slate-600 capitalize">{m.relationship || "—"}</td>
                        <td className="px-3 py-3 text-slate-600">
                          {[m.age !== "" ? `${m.age} yrs` : "", m.gender].filter(Boolean).join(" · ") || "—"}
                          {m.dob && <div className="text-[11px] text-slate-400">DOB {m.dob}</div>}
                        </td>
                        <td className="px-3 py-3 text-slate-600">{m.loanNumber || "—"}</td>
                        <td className="px-3 py-3 text-slate-600 text-xs">
                          <div>{m.mobile || "—"}</div>
                          {m.altMobile && <div className="text-slate-400">Alt: {m.altMobile}</div>}
                          {m.email && <div className="text-slate-400">{m.email}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-3 border-t border-slate-100 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm hover:bg-slate-50">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Paid modal — collects payment_transaction_id required by
// POST /finance/families/{id}/paid/
// ---------------------------------------------------------------------------
function PaidModal({ ids, txId, setTxId, paying, onClose, onConfirm }) {
  useEffect(() => {
    if (!ids) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !paying) onClose();
      if (e.key === "Enter" && !paying) onConfirm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ids, paying, onClose, onConfirm]);

  if (!ids) return null;
  const count = ids.length;
  const valid = txId.trim() !== "";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={() => !paying && onClose()}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-slate-100">
          <div>
            <h3 className="text-base font-semibold text-slate-800">
              Mark {count === 1 ? "family" : `${count} families`} as paid
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Enter the payment transaction ID to confirm.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={paying}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-40"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5">
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            Payment transaction ID <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={txId}
            onChange={(e) => setTxId(e.target.value)}
            placeholder="Enter payment transaction ID"
            disabled={paying}
            autoFocus
            className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 placeholder:text-slate-400 disabled:bg-slate-50"
          />
        </div>

        <div className="px-6 py-3 border-t border-slate-100 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={paying}
            className="px-4 py-2 rounded-lg border border-slate-200 text-sm hover:bg-slate-50 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={paying || !valid}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
          >
            {paying && <Loader2 className="w-4 h-4 animate-spin" />}
            {paying ? "Marking paid..." : "Paid"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activate modal — collects received_transaction_id + received_payment_method
// + received_bank_name, submitted once for ALL selected families via
// POST /finance/families/bulk-activate/
// ---------------------------------------------------------------------------
const RECEIVED_PAYMENT_METHODS = [
  { value: "UPI", label: "UPI" },
  { value: "BANK_TRANSFER", label: "Bank Transfer" },
];

// CHANGED: new `error` prop (inline error), count display, confirmation message.
function ActivateModal({ ids, txId, setTxId, method, setMethod, bankName, setBankName, busy, error, onClose, onConfirm }) {
  useEffect(() => {
    if (!ids) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose();
      if (e.key === "Enter" && !busy) onConfirm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ids, busy, onClose, onConfirm]);

  if (!ids) return null;
  const count = ids.length;
  const valid = txId.trim() !== "" && String(method || "").trim() !== "" && bankName.trim() !== "";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={() => !busy && onClose()}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-slate-100">
          <div>
            <h3 className="text-base font-semibold text-slate-800">
              Activate {count === 1 ? "family" : `${count} families`}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Enter the received transaction details to confirm.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-40"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Selected count */}
          <div className="flex items-center gap-2 rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2 text-sm text-indigo-800">
            <Users className="w-4 h-4 shrink-0" />
            <span>
              <b>{count}</b> {count === 1 ? "family" : "families"} selected
            </span>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">
              Received transaction ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={txId}
              onChange={(e) => setTxId(e.target.value)}
              placeholder="e.g. UPI123456789"
              disabled={busy}
              autoFocus
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 placeholder:text-slate-400 disabled:bg-slate-50"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">
              Received payment method <span className="text-red-500">*</span>
            </label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              disabled={busy}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-slate-50"
            >
              <option value="">— Select method —</option>
              {RECEIVED_PAYMENT_METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">
              Bank Name <span className="text-red-500">*</span>
            </label>

            <input
              type="text"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="Enter bank name, e.g. HDFC Bank"
              maxLength={150}
              disabled={busy}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 placeholder:text-slate-400 disabled:bg-slate-50"
            />
          </div>

          {/* Confirmation message */}
          <p className="text-xs text-slate-500">
            {count === 1
              ? "1 family will be activated under this transaction."
              : `${count} families will be activated under the same transaction.`}
          </p>

          {/* Inline validation / backend error (modal stays open so the user can retry) */}
          {error && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700" role="alert">
              <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-100 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 rounded-lg border border-slate-200 text-sm hover:bg-slate-50 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy || !valid}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? "Activating..." : count > 1 ? `Activate ${count} families` : "Activate"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
export default function MfiFinanceActivationPage() {
  // Clients
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [clientsError, setClientsError] = useState(null);

  // Packages
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [packagesError, setPackagesError] = useState(null);

  // Selections
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedPackageId, setSelectedPackageId] = useState("");

  // Tabs: 'pending' | 'activated' | 'transactions' | 'paid-family-transactions'
  const [activeTab, setActiveTab] = useState("pending");

  // Families
  const [pendingFamilies, setPendingFamilies] = useState([]);
  const [activatedFamilies, setActivatedFamilies] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [loadingActivated, setLoadingActivated] = useState(false);
  const [pendingError, setPendingError] = useState(null);
  const [activatedError, setActivatedError] = useState(null);

  // Transactions
  const [transactions, setTransactions] = useState([]);
  const [txSummary, setTxSummary] = useState(null);
  const [loadingTx, setLoadingTx] = useState(false);
  const [txError, setTxError] = useState(null);
  const [txStatus, setTxStatus] = useState("");

  // Selection / activation
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [paidSelectedIds, setPaidSelectedIds] = useState(new Set());
  const [viewFamily, setViewFamily] = useState(null);
  const [activating, setActivating] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [paidModalIds, setPaidModalIds] = useState(null);
  const [paymentTxId, setPaymentTxId] = useState("");
  const [activateModalIds, setActivateModalIds] = useState(null);
  const [receivedTxId, setReceivedTxId] = useState("");
  const [receivedPayMethod, setReceivedPayMethod] = useState("UPI");
  const [receivedBankName, setReceivedBankName] = useState("");
  const [activateError, setActivateError] = useState(""); // NEW: inline error inside the Activate modal

  // Search states — frontend filtering, no extra API calls
  const [pendingSearch, setPendingSearch] = useState("");
  const [txSearch, setTxSearch] = useState("");

  const [toast, setToast] = useState(null);
  const notify = (message, type = "success") => setToast({ message, type });

  const pendingKeyRef = useRef(null);
  const activatedKeyRef = useRef(null);
  // NEW: synchronous lock so a double click / Enter-key repeat can never send two requests
  const activatingRef = useRef(false);

  // ------------------------------ Clients ---------------------------------
  const loadClients = useCallback(async () => {
    setLoadingClients(true);
    setClientsError(null);
    try {
      const data = await apiRequest(`${API_BASE}/mfi-clients/`);
      setClients(toArrayPayload(data).map(normalizeClient));
    } catch (err) {
      setClientsError(err.message || "Could not load MFI clients");
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // ------------------------------ Packages --------------------------------
  const loadPackages = useCallback(async (clientId) => {
    if (!clientId) {
      setPackages([]);
      return;
    }
    setLoadingPackages(true);
    setPackagesError(null);
    try {
      const data = await apiRequest(`${API_BASE}/mfi-clients/${encodeURIComponent(clientId)}/packages/`);
      setPackages(toArrayPayload(data).map(normalizePackage));
    } catch (err) {
      setPackagesError(err.message || "Could not load packages");
      setPackages([]);
    } finally {
      setLoadingPackages(false);
    }
  }, []);

  // Client changed -> reset everything below it and load its packages
  useEffect(() => {
    setSelectedPackageId("");
    setPendingFamilies([]);
    setActivatedFamilies([]);
    setPendingError(null);
    setActivatedError(null);
    setSelectedIds(new Set());
    setPaidSelectedIds(new Set());
    setPendingSearch("");
    pendingKeyRef.current = null;
    activatedKeyRef.current = null;
    loadPackages(selectedClientId);
  }, [selectedClientId, loadPackages]);

  // ------------------------------ Families --------------------------------
  const loadPending = useCallback(async (clientId, packageId, force = false) => {
    if (!clientId || !packageId) return;
    const key = `${clientId}::${packageId}`;
    if (!force && pendingKeyRef.current === key) return;

    setLoadingPending(true);
    setPendingError(null);
    try {
      const data = await apiRequest(
        `${API_BASE}/finance/families/pending/?client_id=${encodeURIComponent(clientId)}&package=${encodeURIComponent(packageId)}`
      );
      const list = extractFamilies(data);
      setPendingFamilies(list);
      pendingKeyRef.current = key;
      setSelectedIds((prev) => {
        const valid = new Set(list.map((f) => String(f.id)));
        return new Set([...prev].filter((id) => valid.has(id)));
      });
    } catch (err) {
      setPendingError(err.message || "Could not load pending families");
      setPendingFamilies([]);
    } finally {
      setLoadingPending(false);
    }
  }, []);

  const loadActivated = useCallback(async (clientId, packageId, force = false) => {
    if (!clientId || !packageId) return;
    const key = `${clientId}::${packageId}`;
    if (!force && activatedKeyRef.current === key) return;

    setLoadingActivated(true);
    setActivatedError(null);
    try {
      const data = await apiRequest(
        `${API_BASE}/finance/families/activated/?client_id=${encodeURIComponent(clientId)}&package=${encodeURIComponent(packageId)}`
      );
      const list = extractFamilies(data);
      setActivatedFamilies(list);
      activatedKeyRef.current = key;
      setPaidSelectedIds((prev) => {
        const valid = new Set(list.map((f) => String(f.id)));
        return new Set([...prev].filter((id) => valid.has(String(id))));
      });
    } catch (err) {
      setActivatedError(err.message || "Could not load activated families");
      setActivatedFamilies([]);
    } finally {
      setLoadingActivated(false);
    }
  }, []);

  // ---------------------------- Transactions ------------------------------
  const loadTransactions = useCallback(async () => {
    setLoadingTx(true);
    setTxError(null);
    try {
      const params = new URLSearchParams();
      if (selectedClientId) params.set("client_id", selectedClientId);
      if (selectedPackageId) params.set("package", selectedPackageId);
      if (txStatus) params.set("status", txStatus);
      const qs = params.toString();
      const data = await apiRequest(`${API_BASE}/finance/transactions/${qs ? `?${qs}` : ""}`);
      setTransactions(toArrayPayload(data));
      setTxSummary(data?.summary || null);
    } catch (err) {
      setTxError(err.message || "Could not load transactions");
      setTransactions([]);
      setTxSummary(null);
    } finally {
      setLoadingTx(false);
    }
  }, [selectedClientId, selectedPackageId, txStatus]);

  // Fetch the visible tab's data whenever tab / selections change
  // NOTE: paid-family-transactions tab owns its own fetching (no parent fetch —
  // avoids duplicate API calls).
  useEffect(() => {
    if (activeTab === "transactions") {
      loadTransactions();
    } else if (selectedClientId && selectedPackageId) {
      if (activeTab === "pending") loadPending(selectedClientId, selectedPackageId);
      else if (activeTab === "activated") loadActivated(selectedClientId, selectedPackageId);
    }
  }, [activeTab, selectedClientId, selectedPackageId, loadPending, loadActivated, loadTransactions]);

  // ------------------------------ Filtered lists (frontend search) -------
  const filteredPendingFamilies = useMemo(() => {
    const q = pendingSearch.trim().toLowerCase();
    if (!q) return pendingFamilies;
    return pendingFamilies.filter((fam) => {
      const addr = fam.address ? addressLine(fam.address) + " " + [fam.address.address_line_1, fam.address.address_line_2, fam.address.village, fam.address.block, fam.address.district, fam.address.state, fam.address.pincode, fam.address.gram_panchayat].filter(Boolean).join(" ") : "";
      const haystack = [fam.patientName, fam.familyHead, fam.familyIdStr, String(fam.id), fam.primaryMobile, fam.packageName, addr, String(fam.memberCount), (fam.members || []).map((m) => m.name).join(" ")].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [pendingFamilies, pendingSearch]);

  const filteredTransactions = useMemo(() => {
    const q = txSearch.trim().toLowerCase();
    if (!q) return transactions;
    return transactions.filter((t) => {
      const famId = t.family?.family_id || t.family?.familyId || "";
      const famMobile = t.family?.primary_mobile || t.family?.mobile || t.family?.phone || "";
      const famAddr = t.family?.address ? addressLine(t.family.address) : "";
      const pkgName = t.package?.name || t.package?.package_name || "";
      const activatedBy = t.activated_by?.name || t.activated_by?.client_id || "";
      const haystack = [t.transaction_id || String(t.id), famId, famMobile, pkgName, t.status || "", t.transaction_type || "", activatedBy, t.remarks || "", famAddr].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [transactions, txSearch]);

  // ------------------------------ Selection -------------------------------
  // Selection respects filtered view when searching, otherwise full list
  const cappedIds = (pendingSearch.trim() ? filteredPendingFamilies : pendingFamilies).slice(0, MAX_SELECT).map((f) => String(f.id));
  const allSelected = cappedIds.length > 0 && cappedIds.every((id) => selectedIds.has(id));

  const toggleSelect = (id) => {
    const sId = String(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sId)) next.delete(sId);
      else if (next.size >= MAX_SELECT) notify(`You can select up to ${MAX_SELECT} families at once`, "error");
      else next.add(sId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) return setSelectedIds(new Set());
    if (pendingFamilies.length > MAX_SELECT) notify(`Selected first ${MAX_SELECT} families (maximum allowed)`);
    setSelectedIds(new Set(cappedIds));
  };

  // ------------------------------ Paid selection (Activated tab) --------
  const paidCappedIds = activatedFamilies.slice(0, MAX_SELECT).map((f) => String(f.id));
  const allPaidSelected = paidCappedIds.length > 0 && paidCappedIds.every((id) => paidSelectedIds.has(id));

  const togglePaidSelect = (id) => {
    const sId = String(id);
    setPaidSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sId)) next.delete(sId);
      else if (next.size >= MAX_SELECT) notify(`You can select up to ${MAX_SELECT} families at once`, "error");
      else next.add(sId);
      return next;
    });
  };

  const togglePaidSelectAll = () => {
    if (allPaidSelected) return setPaidSelectedIds(new Set());
    if (activatedFamilies.length > MAX_SELECT) notify(`Selected first ${MAX_SELECT} families (maximum allowed)`);
    setPaidSelectedIds(new Set(paidCappedIds));
  };

  const markFamiliesPaid = async (ids, transactionId) => {
    if (!ids || ids.length === 0) return notify("Select at least one family to mark as paid", "error");
    const tx = String(transactionId ?? "").trim();
    if (!tx) return notify("payment_transaction_id is required.", "error");

    setPaying(true);
    setPayingId(ids.length === 1 ? String(ids[0]) : null);
    let ok = 0;
    const failed = [];

    try {
      for (let i = 0; i < ids.length; i += 5) {
        const batch = ids.slice(i, i + 5);
        const results = await Promise.allSettled(
          batch.map((id) =>
            apiRequest(`${API_BASE}/finance/families/${encodeURIComponent(id)}/paid/`, {
              method: "POST",
              body: JSON.stringify({ payment_transaction_id: tx }),
            })
          )
        );
        results.forEach((r, idx) => {
          if (r.status === "fulfilled") ok += 1;
          else failed.push({ id: batch[idx], msg: r.reason?.message });
        });
      }

      if (failed.length === 0) {
        notify(`${ok} ${ok === 1 ? "family" : "families"} marked as paid successfully.`);
      } else if (ok === 0) {
        notify(failed[0].msg || "Mark as paid failed", "error");
      } else {
        notify(`${ok} marked as paid, ${failed.length} failed. ${failed[0].msg || ""}`, "error");
      }

      // Failed ones stay selected so the user can retry
      setPaidSelectedIds(new Set(failed.map((f) => String(f.id))));
      activatedKeyRef.current = null;
      await loadActivated(selectedClientId, selectedPackageId, true);
    } finally {
      setPaying(false);
      setPayingId(null);
    }
  };

  const handleMarkPaidBulk = () => {
    if (paidSelectedIds.size === 0) return notify("Select at least one family to mark as paid", "error");
    setPaymentTxId("");
    setPaidModalIds([...paidSelectedIds]);
  };

  const handleMarkPaidSingle = (id) => {
    setPaymentTxId("");
    setPaidModalIds([String(id)]);
  };

  const handlePaidConfirm = async () => {
    const tx = paymentTxId.trim();
    if (!tx) return notify("Please enter the payment transaction ID.", "error");
    const ids = paidModalIds || [];
    setPaidModalIds(null);
    setPaymentTxId("");
    await markFamiliesPaid(ids, tx);
  };

  const handleClientChange = (e) => setSelectedClientId(e.target.value);

  const handlePackageChange = (e) => {
    setSelectedPackageId(e.target.value);
    setSelectedIds(new Set());
    setPaidSelectedIds(new Set());
    setPendingSearch("");
    pendingKeyRef.current = null;
    activatedKeyRef.current = null;
  };

  // ------------------------------ Activate (BULK) -------------------------
  // Opens a modal to collect received_transaction_id + received_payment_method
  // + received_bank_name, then sends ONE request:
  //   POST /finance/families/bulk-activate/
  const handleActivate = () => {
    if (selectedIds.size === 0) {
      return notify("Select at least one family to activate", "error");
    }

    setReceivedTxId("");
    setReceivedPayMethod("UPI");
    setReceivedBankName("");
    setActivateError("");
    setActivateModalIds([...selectedIds]);
  };

  // REPLACED. Validates, then submits. The modal is NOT closed here — it is closed
  // by activateFamilies() only after a successful response.
  const handleActivateConfirm = async () => {
    if (activatingRef.current) return; // duplicate-submit guard

    const tx = receivedTxId.trim();
    const pm = String(receivedPayMethod || "").trim();
    const bankName = receivedBankName.trim();

    const fail = (message) => {
      setActivateError(message);
      notify(message, "error");
    };

    const ids = activateModalIds || [];
    if (ids.length === 0) return fail("Select at least one family to activate.");
    if (!tx) return fail("Please enter the received transaction ID.");
    if (!pm) return fail("Please select the received payment method.");
    if (!RECEIVED_PAYMENT_METHODS.some((m) => m.value === pm)) {
      return fail("Please select a valid payment method (UPI or Bank Transfer).");
    }
    if (!bankName) return fail("Please enter the bank name.");
    if (bankName.length > 150) return fail("Bank name cannot exceed 150 characters.");

    setActivateError("");
    await activateFamilies(ids, {
      received_transaction_id: tx,
      received_payment_method: pm,
      received_bank_name: bankName,
    });
  };

  // REPLACED. One bulk request — no batching of five, no per-family loop.
  // `ids` are the selection keys (String(family.id)); they are resolved to the
  // actual database IDs (family.dbId) before sending.
  const activateFamilies = async (ids, payload) => {
    if (!ids || ids.length === 0) return notify("Select at least one family to activate", "error");
    if (activatingRef.current) return; // duplicate-submit guard

    // Resolve selection keys -> real database IDs (never the display family_id)
    const byKey = new Map(pendingFamilies.map((f) => [String(f.id), f]));
    const requestedKeys = [];
    const familyIds = [];
    for (const key of ids) {
      const fam = byKey.get(String(key));
      if (!fam || fam.dbId === null || fam.dbId === undefined || fam.dbId === "") {
        const msg = "Could not determine the database ID for a selected family. Refresh the list and try again.";
        setActivateError(msg);
        return notify(msg, "error");
      }
      requestedKeys.push(String(key));
      familyIds.push(fam.dbId);
    }

    // Maps response IDs (database IDs) back to selection keys
    const keyByDbId = new Map(pendingFamilies.map((f) => [String(f.dbId), String(f.id)]));
    const removeFromSelection = (keysToRemove) => {
      const drop = new Set(keysToRemove);
      setSelectedIds((prev) => new Set([...prev].filter((k) => !drop.has(k))));
    };
    const finishModal = (remainingKeys) => {
      if (remainingKeys.length === 0) {
        setActivateModalIds(null);
        setReceivedTxId("");
        setReceivedPayMethod("UPI");
        setReceivedBankName("");
        setActivateError("");
      } else {
        // Keep the modal open for the families that were NOT activated, so the user can retry
        setActivateModalIds(remainingKeys);
      }
    };
    const refreshLists = async () => {
      pendingKeyRef.current = null;
      activatedKeyRef.current = null;
      await Promise.all([
        loadPending(selectedClientId, selectedPackageId, true),
        loadActivated(selectedClientId, selectedPackageId, true),
      ]);
    };
    // Resolve activated_family_ids from a response body (success or error body)
    const activatedKeysFrom = (body) =>
      Array.isArray(body?.activated_family_ids)
        ? body.activated_family_ids
            .map((dbId) => keyByDbId.get(String(dbId)))
            .filter((k) => k && requestedKeys.includes(k))
        : [];

    activatingRef.current = true;
    setActivating(true);

    try {
      const data = await apiRequest(`${API_BASE}/finance/families/bulk-activate/`, {
        method: "POST",
        body: JSON.stringify({
          family_ids: familyIds,
          received_transaction_id: payload.received_transaction_id,
          received_payment_method: payload.received_payment_method,
          received_bank_name: payload.received_bank_name,
        }),
      });

      const failedFamilies = Array.isArray(data?.failed_families) ? data.failed_families : [];
      const hasActivatedList = Array.isArray(data?.activated_family_ids);
      const activatedKeys = activatedKeysFrom(data);
      const reportedSuccess = data?.success === true;

      // Decide which selected families actually succeeded
      let succeededKeys;
      if (hasActivatedList) {
        succeededKeys = activatedKeys;
      } else if (reportedSuccess && failedFamilies.length === 0) {
        succeededKeys = requestedKeys; // backend said success and reported no failures
      } else {
        succeededKeys = [];
      }
      const remainingKeys = requestedKeys.filter((k) => !succeededKeys.includes(k));
      const fullSuccess = reportedSuccess && failedFamilies.length === 0 && remainingKeys.length === 0;

      if (fullSuccess) {
        notify(
          data?.message ||
            `${succeededKeys.length} ${succeededKeys.length === 1 ? "family" : "families"} activated successfully.`
        );
        removeFromSelection(succeededKeys);
        finishModal([]);
      } else {
        // Backend reported a failure or a partial result: never show a false success
        const detail = data?.message || data?.error || data?.detail || "Activation could not be completed.";
        const msg =
          succeededKeys.length > 0
            ? `${succeededKeys.length} activated, ${remainingKeys.length} not activated. ${detail}`
            : detail;
        setActivateError(msg);
        notify(msg, "error");
        removeFromSelection(succeededKeys); // only the confirmed ones leave the selection
        finishModal(remainingKeys);
      }

      // Refresh pending + activated lists (covers partial results too)
      await refreshLists();
    } catch (err) {
      // HTTP error / network error / expired session. Selection is NOT cleared and the modal stays open.
      const body = err?.data;
      const partialKeys = activatedKeysFrom(body); // only if the backend reports some IDs as activated
      const msg = err?.message || "Activation failed. Please try again.";
      setActivateError(msg);
      notify(msg, "error");

      if (partialKeys.length > 0) {
        removeFromSelection(partialKeys);
        finishModal(requestedKeys.filter((k) => !partialKeys.includes(k)));
        await refreshLists();
      }
    } finally {
      activatingRef.current = false;
      setActivating(false);
    }
  };

  const refreshCurrent = () => {
    // Paid Family Transactions tab owns its own refresh inside its component.
    if (activeTab === "transactions") return loadTransactions();
    if (activeTab === "pending") return loadPending(selectedClientId, selectedPackageId, true);
    if (activeTab === "activated") return loadActivated(selectedClientId, selectedPackageId, true);
    return null;
  };

  const canShowFamilies = selectedClientId && selectedPackageId;
  const tabClass = (tab, activeColor) =>
    `px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
      activeTab === tab ? "bg-white shadow-sm text-indigo-700 border border-slate-200" : "text-slate-600 hover:text-slate-800"
    }`;
  const badge = (tab, count, color) =>
    count > 0 && (
      <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${activeTab === tab ? color : "bg-slate-200 text-slate-600"}`}>
        {count}
      </span>
    );

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-24" style={{ fontFamily: "Inter, sans-serif" }}>
      <Toast toast={toast} onClose={() => setToast(null)} />
      <FamilyModal fam={viewFamily} onClose={() => setViewFamily(null)} />
      <PaidModal
        ids={paidModalIds}
        txId={paymentTxId}
        setTxId={setPaymentTxId}
        paying={paying}
        onClose={() => !paying && setPaidModalIds(null)}
        onConfirm={handlePaidConfirm}
      />
      <ActivateModal
        ids={activateModalIds}
        txId={receivedTxId}
        setTxId={(v) => { setReceivedTxId(v); setActivateError(""); }}
        method={receivedPayMethod}
        setMethod={(v) => { setReceivedPayMethod(v); setActivateError(""); }}
        bankName={receivedBankName}
        setBankName={(v) => { setReceivedBankName(v); setActivateError(""); }}
        busy={activating}
        error={activateError}
        onClose={() => !activating && setActivateModalIds(null)}
        onConfirm={handleActivateConfirm}
      />

      {/* Header */}
      <div className="bg-[#0D3366] text-white shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
            <Landmark className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg font-semibold leading-tight">MFI Finance Activation</h1>
            <p className="text-indigo-100 text-xs">Select client and package, then activate pending families</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 px-6 py-6">
          <div className="flex items-center gap-2 mb-4">
            <Wallet className="w-4 h-4 text-indigo-600" />
            <h2 className="font-medium text-slate-800">Select MFI Client & Package</h2>
            {(loadingClients || loadingPackages) && <Loader2 className="w-4 h-4 animate-spin text-slate-400 ml-2" />}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Client — searchable */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Select MFI Client</label>
              <SearchableSelect
                placeholder="— Select MFI Client —"
                searchPlaceholder="Search MFI Client..."
                options={clients}
                value={selectedClientId}
                onChange={handleClientChange}
                getOptionLabel={(c) => c.name}
                getOptionValue={(c) => c.id}
                getOptionSearchText={(c) => `${c.name} ${c.id}`}
                loading={loadingClients}
                error={clientsError}
                onRetry={loadClients}
                emptyText="No MFI clients found."
                disabled={false}
              />
              {!loadingClients && !clientsError && clients.length === 0 && (
                <p className="text-xs text-slate-400 mt-1.5">No MFI clients found.</p>
              )}
            </div>

            {/* Package — searchable, filtered by client */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Select Package</label>
              <SearchableSelect
                placeholder="— Select Package —"
                searchPlaceholder="Search Package..."
                options={packages}
                value={selectedPackageId}
                onChange={handlePackageChange}
                getOptionLabel={(p) => p.label}
                getOptionValue={(p) => p.id}
                getOptionSearchText={(p) => `${p.label} ${p.name} ${p.id}`}
                disabled={!selectedClientId}
                disabledPlaceholder="Select a client first"
                loading={loadingPackages}
                error={packagesError}
                onRetry={() => loadPackages(selectedClientId)}
                emptyText="No packages found for this client."
              />
              {selectedClientId && !loadingPackages && !packagesError && packages.length === 0 && (
                <p className="text-xs text-slate-400 mt-1.5">No packages found for this client.</p>
              )}
            </div>
          </div>
        </div>

        {/* Tabs card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between flex-wrap gap-2 px-6 py-3 border-b border-slate-100 bg-slate-50/60">
            <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-xl">
              <button onClick={() => setActiveTab("pending")} className={tabClass("pending")}>
                Pending Activation
                {badge("pending", pendingFamilies.length, "bg-indigo-100 text-indigo-700")}
              </button>
              <button onClick={() => setActiveTab("activated")} className={tabClass("activated")}>
                Activated
                {badge("activated", activatedFamilies.length, "bg-emerald-100 text-emerald-700")}
              </button>
              <button onClick={() => setActiveTab("transactions")} className={tabClass("transactions")}>
                Transactions
                {badge("transactions", transactions.length, "bg-indigo-100 text-indigo-700")}
              </button>
              <button onClick={() => setActiveTab("paid-family-transactions")} className={tabClass("paid-family-transactions")}>
                Paid Family Transactions
              </button>
            </div>

            <div className="flex items-center gap-2">
              {activeTab === "pending" && (
                <>
                  <span className="text-xs text-slate-500 hidden sm:inline">
                    {selectedIds.size > 0 ? `${selectedIds.size} selected` : ""}
                  </span>
                  <button
                    onClick={handleActivate}
                    disabled={selectedIds.size === 0 || activating}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {activating && <Loader2 className="w-4 h-4 animate-spin" />}
                    {activating ? "Activating..." : `Activate${selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}`}
                  </button>
                </>
              )}
              {activeTab === "activated" && (
                <>
                  <span className="text-xs text-slate-500 hidden sm:inline">
                    {paidSelectedIds.size > 0 ? `${paidSelectedIds.size} selected` : ""}
                  </span>
                  <button
                    onClick={handleMarkPaidBulk}
                    disabled={paidSelectedIds.size === 0 || paying}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {paying && <Loader2 className="w-4 h-4 animate-spin" />}
                    {paying ? "Marking paid..." : `Paid${paidSelectedIds.size > 0 ? ` (${paidSelectedIds.size})` : ""}`}
                  </button>
                </>
              )}
              {activeTab === "transactions" && (
                <select
                  value={txStatus}
                  onChange={(e) => setTxStatus(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"
                >
                  <option value="">All statuses</option>
                  <option value="RECEIVED">Received</option>
                  <option value="PENDING">Pending</option>
                  <option value="FAILED">Failed</option>
                </select>
              )}
              <button
                onClick={refreshCurrent}
                disabled={activeTab !== "transactions" && activeTab !== "paid-family-transactions" && !canShowFamilies}
                className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 disabled:opacity-40"
                title="Refresh"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Placeholder for family tabs until both dropdowns are chosen */}
          {activeTab !== "transactions" && activeTab !== "paid-family-transactions" && !canShowFamilies && (
            <div className="px-6 py-12 text-center">
              <div className="mx-auto w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-3">
                <ShieldCheck className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-700">Select an MFI client and a package to view families</p>
              <p className="text-xs text-slate-400 mt-1">Pending and activated families appear once both are chosen.</p>
            </div>
          )}

          {/* Pending */}
          {activeTab === "pending" && canShowFamilies && (
            <div className="overflow-x-auto">
              {/* Pending search — frontend filter, preserves existing data */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 px-6 py-3 bg-white border-b border-slate-100">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    value={pendingSearch}
                    onChange={(e) => setPendingSearch(e.target.value)}
                    placeholder="Search by family name, family ID, mobile, location, package..."
                    className="w-full pl-9 pr-8 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder:text-slate-400"
                  />
                  {pendingSearch && (
                    <button
                      onClick={() => setPendingSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-slate-400"
                      aria-label="Clear search"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {pendingSearch && (
                  <span className="text-xs text-slate-500 whitespace-nowrap">
                    {filteredPendingFamilies.length} of {pendingFamilies.length} shown
                  </span>
                )}
                {!pendingSearch && pendingFamilies.length > 0 && (
                  <span className="text-xs text-slate-400 whitespace-nowrap hidden sm:inline">
                    {pendingFamilies.length} pending families
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between px-6 py-2.5 bg-white border-b border-slate-100">
                <button
                  onClick={toggleSelectAll}
                  disabled={filteredPendingFamilies.length === 0 || loadingPending}
                  className="flex items-center gap-2 text-sm font-medium text-slate-700 disabled:opacity-40"
                >
                  {allSelected ? <CheckSquare className="w-4 h-4 text-indigo-600" /> : <Square className="w-4 h-4 text-slate-400" />}
                  Select all {filteredPendingFamilies.length > MAX_SELECT ? `(first ${MAX_SELECT})` : pendingSearch ? `(${filteredPendingFamilies.length} filtered)` : ""}
                </button>
                <span className="text-xs text-slate-400">
                  {pendingSearch ? `${filteredPendingFamilies.length} filtered` : pendingFamilies.length > 0 ? `${pendingFamilies.length} pending families` : ""}
                </span>
              </div>

              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-left">
                    <th className="px-6 py-3 font-medium w-10">
                      <span className="sr-only">Select</span>
                    </th>
                    <th className="px-4 py-3 font-medium">Patient</th>
                    <th className="px-4 py-3 font-medium">Members</th>
                    <th className="px-4 py-3 font-medium">Package</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <StateRow
                    colSpan={6}
                    loading={loadingPending}
                    error={pendingError}
                    empty={filteredPendingFamilies.length === 0}
                    loadingText="Loading pending families..."
                    emptyText={pendingSearch ? `No pending families match "${pendingSearch}"` : "No pending families for this client and package."}
                    onRetry={() => loadPending(selectedClientId, selectedPackageId, true)}
                  />
                  {!loadingPending &&
                    !pendingError &&
                    filteredPendingFamilies.map((fam) => {
                      const checked = selectedIds.has(String(fam.id));
                      return (
                        <tr
                          key={String(fam.id)}
                          onClick={() => toggleSelect(fam.id)}
                          className={`border-t border-slate-100 cursor-pointer transition-colors ${
                            checked ? "bg-indigo-50/60 hover:bg-indigo-50" : "hover:bg-slate-50/60"
                          }`}
                        >
                          <td className="px-6 py-3" onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => toggleSelect(fam.id)} aria-label={`Select ${fam.patientName || fam.familyHead}`}>
                              {checked ? <CheckSquare className="w-4 h-4 text-indigo-600" /> : <Square className="w-4 h-4 text-slate-400" />}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <PatientCell fam={fam} />
                          </td>
                          <td className="px-4 py-3 text-slate-600">{fam.memberCount}</td>
                          <td className="px-4 py-3 text-slate-600">{fam.packageName}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Pending</span>
                          </td>
                          <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => setViewFamily(fam)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50"
                            >
                              <Eye className="w-3.5 h-3.5" /> View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}

          {/* Activated */}
          {activeTab === "activated" && canShowFamilies && (
            <div className="overflow-x-auto">
              <div className="flex items-center justify-between px-6 py-2.5 bg-white border-b border-slate-100">
                <button
                  onClick={togglePaidSelectAll}
                  disabled={activatedFamilies.length === 0 || loadingActivated}
                  className="flex items-center gap-2 text-sm font-medium text-slate-700 disabled:opacity-40"
                >
                  {allPaidSelected ? <CheckSquare className="w-4 h-4 text-indigo-600" /> : <Square className="w-4 h-4 text-slate-400" />}
                  Select all {activatedFamilies.length > MAX_SELECT ? `(first ${MAX_SELECT})` : ""}
                </button>
                <span className="text-xs text-slate-400">
                  {activatedFamilies.length > 0 ? `${activatedFamilies.length} activated families` : "No activated families"}
                </span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-left">
                    <th className="px-6 py-3 font-medium w-10">
                      <span className="sr-only">Select</span>
                    </th>
                    <th className="px-4 py-3 font-medium">Family</th>
                    <th className="px-4 py-3 font-medium">Members</th>
                    <th className="px-4 py-3 font-medium">Package</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Activated on</th>
                    <th className="px-4 py-3 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <StateRow
                    colSpan={7}
                    loading={loadingActivated}
                    error={activatedError}
                    empty={activatedFamilies.length === 0}
                    loadingText="Loading activated families..."
                    emptyText="No activated families for this client and package."
                    onRetry={() => loadActivated(selectedClientId, selectedPackageId, true)}
                  />
                  {!loadingActivated &&
                    !activatedError &&
                    activatedFamilies.map((fam) => {
                      const checked = paidSelectedIds.has(String(fam.id));
                      const isPayingRow = payingId === String(fam.id) || (paying && paidSelectedIds.has(String(fam.id)));
                      return (
                      <tr
                        key={String(fam.id)}
                        onClick={() => togglePaidSelect(fam.id)}
                        className={`border-t border-slate-100 cursor-pointer transition-colors ${
                          checked ? "bg-indigo-50/60 hover:bg-indigo-50" : "hover:bg-slate-50/60"
                        }`}
                      >
                        <td className="px-6 py-3" onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => togglePaidSelect(fam.id)} aria-label={`Select ${fam.familyHead}`}>
                            {checked ? <CheckSquare className="w-4 h-4 text-indigo-600" /> : <Square className="w-4 h-4 text-slate-400" />}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <FamilyCell fam={fam} />
                        </td>
                        <td className="px-4 py-3 text-slate-600">{fam.memberCount}</td>
                        <td className="px-4 py-3 text-slate-600">{fam.packageName}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Activated</span>
                        </td>
                        <td className="px-4 py-3 text-slate-600 text-xs">{formatDate(fam.activatedAt)}</td>
                        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="inline-flex items-center gap-2">
                            <button
                              onClick={() => setViewFamily(fam)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50"
                            >
                              <Eye className="w-3.5 h-3.5" /> View
                            </button>
                            <button
                              onClick={() => handleMarkPaidSingle(fam.id)}
                              disabled={paying}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
                            >
                              {isPayingRow && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                              Paid
                            </button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}

          {/* Transactions (finance team) */}
          {activeTab === "transactions" && (
            <div className="overflow-x-auto">
              {/* Transactions search — frontend filter */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 px-6 py-3 bg-white border-b border-slate-100">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    value={txSearch}
                    onChange={(e) => setTxSearch(e.target.value)}
                    placeholder="Search by family, transaction ID, mobile, package, status..."
                    className="w-full pl-9 pr-8 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder:text-slate-400"
                  />
                  {txSearch && (
                    <button
                      onClick={() => setTxSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-slate-400"
                      aria-label="Clear search"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {txSearch && (
                  <span className="text-xs text-slate-500 whitespace-nowrap">
                    {filteredTransactions.length} of {transactions.length} shown
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2 px-6 py-2.5 bg-white border-b border-slate-100">
                <span className="text-xs text-slate-500">
                  {txSearch ? `${filteredTransactions.length} of ${transactions.length} transactions` : selectedClientId ? `Filtered by selected client${selectedPackageId ? " and package" : ""}` : "Showing all transactions"}
                </span>
                {txSummary && (
                  <div className="flex items-center gap-4 text-xs text-slate-600">
                    <span>
                      Transactions: <b className="text-slate-800">{txSummary.total_transactions}</b>
                    </span>
                    <span>
                      Total: <b className="text-slate-800">{formatAmount(txSummary.total_amount)}</b>
                    </span>
                  </div>
                )}
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-left">
                    <th className="px-6 py-3 font-medium">Transaction ID</th>
                    <th className="px-4 py-3 font-medium">Family</th>
                    <th className="px-4 py-3 font-medium">Package</th>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium text-right">Amount</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Activated by</th>
                    <th className="px-4 py-3 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  <StateRow
                    colSpan={8}
                    loading={loadingTx}
                    error={txError}
                    empty={filteredTransactions.length === 0}
                    loadingText="Loading transactions..."
                    emptyText={txSearch ? `No transactions match "${txSearch}"` : "No transactions found."}
                    onRetry={loadTransactions}
                  />
                  {!loadingTx &&
                    !txError &&
                    filteredTransactions.map((t) => (
                      <tr key={String(t.id)} className="border-t border-slate-100 hover:bg-slate-50/60 align-top">
                        <td className="px-6 py-3">
                          <div className="font-medium text-slate-800">{t.transaction_id}</div>
                          {t.remarks && <div className="text-[11px] text-slate-400 max-w-[220px]">{t.remarks}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-slate-800">{t.family?.family_id || "—"}</div>
                          <div className="text-xs text-slate-500">
                            {[t.family?.primary_mobile, addressLine(t.family?.address)].filter(Boolean).join(" · ")}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{t.package?.name || "—"}</td>
                        <td className="px-4 py-3 text-slate-600 text-xs">{(t.transaction_type || "").replace(/_/g, " ")}</td>
                        <td className="px-4 py-3 text-right font-medium text-slate-800">{formatAmount(t.amount)}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              t.status === "RECEIVED" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                            }`}
                          >
                            {t.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          <div>{t.activated_by?.name || "—"}</div>
                          <div className="text-[11px] text-slate-400">{t.activated_by?.client_id}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">{formatDate(t.transaction_date)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Paid Family Transactions — reuses existing component (summary + filters + table + detail modal) */}
          {activeTab === "paid-family-transactions" && (
            <div className="px-6 py-6">
              <PaidFamilyTransactionsContent
                selectedPackageId={selectedPackageId}
                selectedClientId={selectedClientId}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}